import { jsx, jsxs } from "react/jsx-runtime";
import { Button, CheckboxWithLabel, DatePicker, Divider, PlainA, Select, Span, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection, cn } from "@pcln/horizon";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { FormAutocompleteField, resolveAutocompleteFieldOptions } from "../shared/components/Autocomplete/FormAutocompleteField.js";
import { SearchFormRadioGroup } from "../shared/components/SearchFormRadioGroup.js";
import { DEFAULT_COLLISION_AVOIDANCE } from "../shared/constants.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { getDateRangeFromFormDates, parseFormDateString } from "../shared/utils/form-date.js";
import { generateTimeSelectOptions } from "../shared/utils/generate-time-options.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { CHECKBOX_TEXT, FLY_DRIVE, RADIO_OPTIONS, STAY_DRIVE, STAY_FLY, STAY_FLY_DRIVE } from "./constants.js";
import { getPartStaySelectedDates, handleDateRangeCalendarChange, handleSingleDateCalendarChange, isString } from "./formUtils.js";
import { getCarPickupSlotClass, getGridContainerClass, horizonPackagesForm } from "./PackageSearchForm.variants.js";
import { formatPackageSearchHref } from "./url-builder.js";
import { createPackagesSearchFormSchema, isInfantLimitReachedPackages, isInfantOnInternationalTravel } from "./validation.js";
const DEFAULT_INITIAL_VALUES = {
    tripType: STAY_FLY,
    partialStay: false,
    startLocation: null,
    endLocation: null,
    startDate: '',
    endDate: '',
    hotelStartDate: '',
    hotelEndDate: '',
    travelers: {
        rooms: 1,
        adults: 2,
        children: 0,
        childrenAges: {},
        lapInfants: 0
    },
    isRecommendedTrip: false,
    includeBasicEconomy: true,
    pickupCarSomewhere: false,
    rcLocation: null,
    rcPickupDate: '',
    rcDropoffDate: '',
    rcPickupTime: '10:00',
    rcDropoffTime: '10:00'
};
function PackageSearchForm({ onSubmit, initialValues, isMobile = false, renderAutocomplete, autocompleteOptions, localeCode, pageName = 'homepage', onItemClick, autoAdvance = false, defaultPickupTime = '10:00', defaultDropoffTime = '10:00', useFlightTypeaheadForFlyDrive = false, advancedSearchCopy = 'Advanced Search (One-Way Flight, Multi-Hotel)', onAdvancedSearchClick, onAdvancedSearchVisible, hotelCarCheckboxLabel, onHotelCarCheckboxToggle, partialStayCheckboxLabel, onPartialStayCheckboxToggle, onTripTypeChange, onSearchCriteriaChange, shouldAllowInternationalLapInfants = false }) {
    const mergedInitialValues = useMemo(()=>({
            ...DEFAULT_INITIAL_VALUES,
            rcPickupTime: defaultPickupTime,
            rcDropoffTime: defaultDropoffTime,
            ...initialValues
        }), [
        initialValues,
        defaultPickupTime,
        defaultDropoffTime
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const validationSchema = useMemo(()=>createPackagesSearchFormSchema({
            shouldAllowInternationalLapInfants
        }), [
        shouldAllowInternationalLapInfants
    ]);
    const { radioButtons, originInput, destinationInput, timelineInput, passengersInput, advancedSearchSlot, checkBoxSlot, partStaySlot } = horizonPackagesForm();
    const { clearErrors, control, formState: { errors }, handleSubmit, setValue, trigger } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(validationSchema),
        reValidateMode: 'onChange'
    });
    const watchTripType = useWatch({
        control,
        name: 'tripType',
        defaultValue: mergedInitialValues.tripType
    });
    const watchStartLocation = useWatch({
        control,
        name: 'startLocation',
        defaultValue: mergedInitialValues.startLocation
    });
    const watchEndLocation = useWatch({
        control,
        name: 'endLocation',
        defaultValue: mergedInitialValues.endLocation
    });
    const watchStartDate = useWatch({
        control,
        name: 'startDate',
        defaultValue: mergedInitialValues.startDate
    });
    const watchEndDate = useWatch({
        control,
        name: 'endDate',
        defaultValue: mergedInitialValues.endDate
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: mergedInitialValues.travelers
    });
    const watchHotelStartDate = useWatch({
        control,
        name: 'hotelStartDate',
        defaultValue: mergedInitialValues.hotelStartDate
    });
    const watchHotelEndDate = useWatch({
        control,
        name: 'hotelEndDate',
        defaultValue: mergedInitialValues.hotelEndDate
    });
    const watchPartialStay = useWatch({
        control,
        name: 'partialStay',
        defaultValue: mergedInitialValues.partialStay
    });
    const watchRcLocation = useWatch({
        control,
        name: 'rcLocation',
        defaultValue: mergedInitialValues.rcLocation
    });
    const watchRcPickupDate = useWatch({
        control,
        name: 'rcPickupDate',
        defaultValue: mergedInitialValues.rcPickupDate
    });
    const watchRcDropoffDate = useWatch({
        control,
        name: 'rcDropoffDate',
        defaultValue: mergedInitialValues.rcDropoffDate
    });
    useEffect(()=>{
        onSearchCriteriaChange?.({
            tripType: watchTripType,
            startLocation: watchStartLocation ?? null,
            endLocation: watchEndLocation ?? null
        });
    }, [
        onSearchCriteriaChange,
        watchEndLocation,
        watchStartLocation,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('startLocation');
    }, [
        clearErrors,
        watchStartLocation
    ]);
    useEffect(()=>{
        clearErrors('endLocation');
    }, [
        clearErrors,
        watchEndLocation
    ]);
    useEffect(()=>{
        clearErrors('rcLocation');
    }, [
        clearErrors,
        watchRcLocation
    ]);
    useEffect(()=>{
        clearErrors('startDate');
    }, [
        clearErrors,
        watchStartDate
    ]);
    useEffect(()=>{
        clearErrors('endDate');
    }, [
        clearErrors,
        watchEndDate
    ]);
    useEffect(()=>{
        clearErrors('hotelStartDate');
    }, [
        clearErrors,
        watchHotelStartDate
    ]);
    useEffect(()=>{
        clearErrors('hotelEndDate');
    }, [
        clearErrors,
        watchHotelEndDate
    ]);
    useEffect(()=>{
        clearErrors('rcPickupDate');
        clearErrors('rcDropoffTime');
    }, [
        clearErrors,
        watchRcPickupDate
    ]);
    useEffect(()=>{
        clearErrors('rcDropoffDate');
        clearErrors('rcDropoffTime');
    }, [
        clearErrors,
        watchRcDropoffDate
    ]);
    const [packagesType, setPackagesType] = useState(mergedInitialValues.tripType);
    const [isHotelPartialStay, setIsHotelPartialStay] = useState(false);
    const [isCarPickupElsewhere, setIsCarPickupElsewhere] = useState(false);
    const [pickupTime, setPickupTime] = useState(defaultPickupTime);
    const [dropoffTime, setDropoffTime] = useState(defaultDropoffTime);
    const dropoffDatePickerTriggerRef = useRef(null);
    const dropoffDatePickerTimeoutRef = useRef(null);
    useEffect(()=>()=>{
            if (dropoffDatePickerTimeoutRef.current) clearTimeout(dropoffDatePickerTimeoutRef.current);
        }, []);
    const hasOnlyHotelAndCar = packagesType === STAY_DRIVE;
    const hasHotelIncluded = packagesType.includes('STAY');
    const hasHotelAndFlightIncluded = packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE;
    const hasHotelPartStayIncluded = isHotelPartialStay && hasHotelAndFlightIncluded;
    const [currentDate] = useState(()=>new Date());
    const calendarMaxMonth = useMemo(()=>addMonths(currentDate, 11), [
        currentDate
    ]);
    const timeSelectOptions = useMemo(()=>generateTimeSelectOptions(localeCode ?? 'en-US'), [
        localeCode
    ]);
    const hasFiredAdvancedSearchVisibleRef = useRef(false);
    useEffect(()=>{
        if (!hasOnlyHotelAndCar && !hasFiredAdvancedSearchVisibleRef.current) {
            hasFiredAdvancedSearchVisibleRef.current = true;
            onAdvancedSearchVisible?.();
        }
    }, [
        hasOnlyHotelAndCar,
        onAdvancedSearchVisible
    ]);
    const parsedTripStartDate = parseFormDateString(watchStartDate, currentDate);
    const parsedTripEndDate = parseFormDateString(watchEndDate, currentDate);
    const partStayMinDate = parsedTripStartDate && parsedTripStartDate > currentDate ? parsedTripStartDate : currentDate;
    const selectedTripDates = useMemo(()=>getDateRangeFromFormDates(watchStartDate, watchEndDate, currentDate), [
        watchStartDate,
        watchEndDate,
        currentDate
    ]);
    const [maxSelectableDate, setMaxSelectableDate] = useState();
    const [maxSelectablePartStayDate, setMaxSelectablePartStayDate] = useState();
    const [defaultMonth, setDefaultMonth] = useState(()=>parseFormDateString(mergedInitialValues.startDate, currentDate) ?? currentDate);
    const [defaultPartStayMonth, setPartStayDefaultMonth] = useState();
    const [defaultPickupMonth, setDefaultPickupMonth] = useState(()=>parseFormDateString(mergedInitialValues.rcPickupDate, currentDate) ?? currentDate);
    const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(()=>parseFormDateString(mergedInitialValues.rcDropoffDate, currentDate) ?? currentDate);
    const onRcPickupDateChange = useCallback((date)=>{
        handleSingleDateCalendarChange(setValue, 'rcPickupDate', setDefaultPickupMonth, date);
        setDefaultDropoffMonth(date);
        if (isMobile) {
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            if (dropoffDatePickerTimeoutRef.current) clearTimeout(dropoffDatePickerTimeoutRef.current);
            dropoffDatePickerTimeoutRef.current = setTimeout(()=>{
                dropoffDatePickerTimeoutRef.current = null;
                dropoffDatePickerTriggerRef.current?.click();
            }, 0);
        }
    }, [
        setValue,
        isMobile
    ]);
    const onRcDropoffDateChange = useCallback((date)=>{
        const formattedDate = format(date, 'yyyyMMdd');
        handleSingleDateCalendarChange(setValue, 'rcDropoffDate', setDefaultDropoffMonth, date);
        if (watchRcPickupDate && formattedDate === watchRcPickupDate) trigger('rcDropoffTime');
    }, [
        setValue,
        trigger,
        watchRcPickupDate
    ]);
    const handleSubmitHelper = handleSubmit((data)=>{
        onSubmit(data);
    });
    const onLocationSelect = useCallback((searchKey, item)=>{
        setValue(searchKey, item);
        if ('rcLocation' === searchKey) clearErrors('rcLocation');
        if (isMobile || !item) {
            if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            onItemClick?.();
            if (autoAdvance) focusNextElement(true);
        }
    }, [
        setValue,
        clearErrors,
        isMobile,
        onItemClick,
        autoAdvance,
        focusNextElement
    ]);
    const advancedSearchHrefComputed = formatPackageSearchHref(pageName, watchTripType, watchStartLocation, watchEndLocation, watchStartDate, watchEndDate, watchTravelers, watchHotelStartDate, watchHotelEndDate, watchPartialStay);
    return /*#__PURE__*/ jsx("div", {
        className: "@container w-auto",
        children: /*#__PURE__*/ jsx("form", {
            className: "w-auto",
            "aria-label": "horizon-packages-search-form",
            onSubmit: handleSubmitHelper,
            children: /*#__PURE__*/ jsx("div", {
                className: "mx-auto w-auto",
                children: /*#__PURE__*/ jsxs("div", {
                    className: getGridContainerClass(packagesType, isHotelPartialStay, isCarPickupElsewhere),
                    children: [
                        /*#__PURE__*/ jsx("div", {
                            className: radioButtons(),
                            children: /*#__PURE__*/ jsx(SearchFormRadioGroup, {
                                wrap: true,
                                name: "packagesType",
                                defaultValue: packagesType,
                                options: RADIO_OPTIONS,
                                handleChange: (value)=>{
                                    if (isString(value)) {
                                        setPackagesType(value);
                                        setValue('tripType', value);
                                        onTripTypeChange?.(value);
                                    }
                                }
                            })
                        }),
                        !hasOnlyHotelAndCar && /*#__PURE__*/ jsx("div", {
                            className: originInput(),
                            children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                                ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                    searchKey: 'startLocation',
                                    typeAheadEndpoint: 'packagesOrigin'
                                }),
                                renderAutocomplete: renderAutocomplete,
                                searchKey: "startLocation",
                                label: "Where from?",
                                typeAheadEndpoint: "packagesOrigin",
                                errorMessage: errors.startLocation?.message,
                                initialSelectedItem: watchStartLocation ?? void 0,
                                onItemSelect: (item)=>onLocationSelect('startLocation', item),
                                inputRef: autoAdvance ? registerTabElement : void 0,
                                collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                            })
                        }),
                        /*#__PURE__*/ jsx("div", {
                            className: destinationInput(),
                            children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                                ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                    searchKey: 'endLocation',
                                    typeAheadEndpoint: useFlightTypeaheadForFlyDrive && packagesType === FLY_DRIVE ? 'flights' : 'hotels'
                                }),
                                renderAutocomplete: renderAutocomplete,
                                searchKey: "endLocation",
                                label: "Where to?",
                                typeAheadEndpoint: useFlightTypeaheadForFlyDrive && packagesType === FLY_DRIVE ? 'flights' : 'hotels',
                                errorMessage: errors.endLocation?.message,
                                initialSelectedItem: watchEndLocation ?? void 0,
                                onItemSelect: (item)=>onLocationSelect('endLocation', item),
                                inputRef: autoAdvance ? registerTabElement : void 0,
                                collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                            })
                        }),
                        /*#__PURE__*/ jsx("div", {
                            "data-testid": "date-picker-departing-returning",
                            className: timelineInput(),
                            children: /*#__PURE__*/ jsx(DatePicker, {
                                label: "Departing - Returning",
                                mode: "range",
                                numberOfMonths: isMobileComponent ? 12 : 2,
                                inputRef: autoAdvance ? registerTabElement : void 0,
                                selected: selectedTripDates,
                                handleCalendarChange: (dates)=>{
                                    handleDateRangeCalendarChange(setValue, 'startDate', 'endDate', setDefaultMonth, dates);
                                    hasHotelIncluded && dates?.from && dates?.to === void 0 ? setMaxSelectableDate(addDays(dates.from, 28)) : setMaxSelectableDate(void 0);
                                    if (isHotelPartialStay && dates?.from && dates?.to) {
                                        const newStart = format(dates.from, 'yyyyMMdd');
                                        const newEnd = format(dates.to, 'yyyyMMdd');
                                        const startOutOfRange = watchHotelStartDate && (watchHotelStartDate < newStart || watchHotelStartDate > newEnd);
                                        const endOutOfRange = watchHotelEndDate && (watchHotelEndDate < newStart || watchHotelEndDate > newEnd);
                                        if (startOutOfRange || endOutOfRange) {
                                            setValue('hotelStartDate', newStart);
                                            setValue('hotelEndDate', newEnd);
                                        }
                                        setMaxSelectablePartStayDate(void 0);
                                    }
                                },
                                errorMessage: errors.startDate?.message || errors.endDate?.message,
                                disabled: {
                                    before: currentDate,
                                    after: maxSelectableDate
                                },
                                startMonth: currentDate,
                                endMonth: calendarMaxMonth,
                                defaultMonth: defaultMonth,
                                min: hasHotelIncluded ? 1 : 0,
                                collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                            })
                        }),
                        hasHotelPartStayIncluded && /*#__PURE__*/ jsx("div", {
                            "data-testid": "date-picker-hotel-part-stay",
                            className: partStaySlot(),
                            children: /*#__PURE__*/ jsx(DatePicker, {
                                label: "Check-in - Check-out",
                                mode: "range",
                                numberOfMonths: isMobileComponent ? 12 : 2,
                                selected: getPartStaySelectedDates(watchHotelStartDate, watchHotelEndDate, watchStartDate, watchEndDate),
                                handleCalendarChange: (dates)=>{
                                    handleDateRangeCalendarChange(setValue, 'hotelStartDate', 'hotelEndDate', setPartStayDefaultMonth, dates);
                                    if (hasHotelIncluded && dates?.from && dates?.to === void 0) {
                                        const twentyEightDayCap = addDays(dates.from, 28);
                                        setMaxSelectablePartStayDate(parsedTripEndDate && parsedTripEndDate < twentyEightDayCap ? parsedTripEndDate : twentyEightDayCap);
                                    } else setMaxSelectablePartStayDate(void 0);
                                },
                                errorMessage: errors.hotelStartDate?.message || errors.hotelEndDate?.message,
                                disabled: {
                                    before: partStayMinDate,
                                    after: maxSelectablePartStayDate && parsedTripEndDate ? maxSelectablePartStayDate < parsedTripEndDate ? maxSelectablePartStayDate : parsedTripEndDate : maxSelectablePartStayDate || parsedTripEndDate
                                },
                                startMonth: partStayMinDate,
                                endMonth: parsedTripEndDate ?? calendarMaxMonth,
                                defaultMonth: defaultPartStayMonth || (parsedTripStartDate ?? defaultMonth),
                                min: 1,
                                collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                            }, `part-stay-trip-${watchStartDate}-${watchEndDate}`)
                        }),
                        /*#__PURE__*/ jsx("div", {
                            className: passengersInput(),
                            children: /*#__PURE__*/ jsx(TravelerSelection, {
                                isHotel: hasOnlyHotelAndCar,
                                initialValues: mergedInitialValues.travelers,
                                roomsEnabled: hasHotelIncluded,
                                ageSelectEnabled: true,
                                maxTravelers: 8,
                                maxChildren: 6,
                                tooltipErrorMessageId: "travelerSelectionError",
                                onChange: (values)=>{
                                    setValue('travelers', values);
                                },
                                errorMessage: isInfantOnInternationalTravel(watchStartLocation, watchEndLocation, watchTravelers.childrenAges, watchTripType, shouldAllowInternationalLapInfants) || isInfantLimitReachedPackages(watchTravelers.adults, watchTravelers.childrenAges, watchTripType) || errors?.travelers?.children?.message,
                                collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE,
                                disclaimer: "You may only book a package for up to 8 travelers."
                            })
                        }),
                        /*#__PURE__*/ jsxs("div", {
                            className: cn('contents', '@7xl:flex @7xl:items-center @7xl:gap-3 @7xl:py-2 @7xl:[grid-area:footer]'),
                            children: [
                                hasHotelIncluded && /*#__PURE__*/ jsx("div", {
                                    className: checkBoxSlot(),
                                    children: /*#__PURE__*/ jsx(CheckboxWithLabel, {
                                        checked: packagesType === STAY_DRIVE ? isCarPickupElsewhere : isHotelPartialStay,
                                        handleChange: ()=>{
                                            if (packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE) {
                                                setValue('partialStay', !isHotelPartialStay);
                                                setIsHotelPartialStay(!isHotelPartialStay);
                                                if (!isHotelPartialStay) {
                                                    setValue('hotelStartDate', watchHotelStartDate || watchStartDate);
                                                    setValue('hotelEndDate', watchHotelEndDate || watchEndDate);
                                                }
                                                onPartialStayCheckboxToggle?.();
                                            }
                                            if (packagesType === STAY_DRIVE) {
                                                setValue('pickupCarSomewhere', !isCarPickupElsewhere);
                                                setIsCarPickupElsewhere(!isCarPickupElsewhere);
                                                if (!isCarPickupElsewhere) {
                                                    if (!watchRcLocation && watchEndLocation) setValue('rcLocation', watchEndLocation);
                                                    if (watchStartDate) {
                                                        setValue('rcPickupDate', watchStartDate);
                                                        setDefaultPickupMonth(parseFormDateString(watchStartDate, currentDate) ?? currentDate);
                                                    }
                                                    if (watchEndDate) {
                                                        setValue('rcDropoffDate', watchEndDate);
                                                        setDefaultDropoffMonth(parseFormDateString(watchEndDate, currentDate) ?? currentDate);
                                                    }
                                                }
                                                onHotelCarCheckboxToggle?.();
                                            }
                                        },
                                        labelNode: /*#__PURE__*/ jsx(Span, {
                                            textStyle: "body3",
                                            children: (()=>{
                                                if (packagesType === STAY_DRIVE && hotelCarCheckboxLabel) return hotelCarCheckboxLabel;
                                                if ((packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE) && partialStayCheckboxLabel) return partialStayCheckboxLabel;
                                                return CHECKBOX_TEXT[packagesType];
                                            })()
                                        }),
                                        size: "16",
                                        name: "checkbox-hotel-part-stay",
                                        value: "hotel"
                                    })
                                }),
                                hasHotelAndFlightIncluded && /*#__PURE__*/ jsx("div", {
                                    className: "hidden h-4 flex-row @7xl:flex",
                                    children: /*#__PURE__*/ jsx(Divider, {
                                        direction: "vertical"
                                    })
                                }),
                                !hasOnlyHotelAndCar && /*#__PURE__*/ jsx("div", {
                                    className: advancedSearchSlot(),
                                    children: /*#__PURE__*/ jsx(PlainA, {
                                        textStyle: "body3",
                                        className: "font-bold",
                                        iconRight: "arrow_forward",
                                        href: advancedSearchHrefComputed,
                                        target: "_blank",
                                        onClick: onAdvancedSearchClick,
                                        children: advancedSearchCopy
                                    })
                                })
                            ]
                        }),
                        isCarPickupElsewhere && hasOnlyHotelAndCar && /*#__PURE__*/ jsxs("div", {
                            className: getCarPickupSlotClass(packagesType, isHotelPartialStay, isCarPickupElsewhere),
                            children: [
                                /*#__PURE__*/ jsx("div", {
                                    className: "[grid-area:city]",
                                    children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                                        ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                            searchKey: 'rcLocation',
                                            typeAheadEndpoint: 'cars'
                                        }),
                                        renderAutocomplete: renderAutocomplete,
                                        searchKey: "rcLocation",
                                        label: "City, Airport or Address",
                                        typeAheadEndpoint: "cars",
                                        errorMessage: errors.rcLocation?.message,
                                        initialSelectedItem: watchRcLocation ?? watchEndLocation ?? void 0,
                                        onItemSelect: (item)=>onLocationSelect('rcLocation', item),
                                        inputRef: autoAdvance ? registerTabElement : void 0,
                                        collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "[grid-area:pickupDate]",
                                    children: /*#__PURE__*/ jsx(DatePicker, {
                                        label: "Pick-up",
                                        mode: "single",
                                        numberOfMonths: isMobileComponent ? 12 : 1,
                                        inputRef: autoAdvance ? registerTabElement : void 0,
                                        selected: parseFormDateString(watchRcPickupDate, currentDate),
                                        handleCalendarChange: onRcPickupDateChange,
                                        errorMessage: errors.rcPickupDate?.message,
                                        disabled: {
                                            before: currentDate
                                        },
                                        startMonth: currentDate,
                                        endMonth: calendarMaxMonth,
                                        defaultMonth: defaultPickupMonth,
                                        collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "[grid-area:pickupTime]",
                                    children: /*#__PURE__*/ jsx(Select, {
                                        label: "Pick-up Time",
                                        isInset: true,
                                        menuItems: timeSelectOptions,
                                        initialValue: pickupTime,
                                        onChange: (item)=>{
                                            setPickupTime(item);
                                            setValue('rcPickupTime', item);
                                            if (watchRcPickupDate && watchRcDropoffDate && watchRcPickupDate === watchRcDropoffDate) trigger('rcDropoffTime');
                                        },
                                        placeholder: "Pick-up Time"
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "[grid-area:dropoffDate]",
                                    children: /*#__PURE__*/ jsx(DatePicker, {
                                        label: "Drop-off",
                                        mode: "single",
                                        numberOfMonths: isMobileComponent ? 12 : 1,
                                        selected: parseFormDateString(watchRcDropoffDate, currentDate),
                                        handleCalendarChange: onRcDropoffDateChange,
                                        errorMessage: errors.rcDropoffDate?.message,
                                        inputRef: (el)=>{
                                            dropoffDatePickerTriggerRef.current = el;
                                        },
                                        disabled: {
                                            before: parseFormDateString(watchRcPickupDate, currentDate) ?? currentDate
                                        },
                                        startMonth: parseFormDateString(watchRcPickupDate, currentDate) ?? currentDate,
                                        endMonth: calendarMaxMonth,
                                        defaultMonth: defaultDropoffMonth,
                                        collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                                    }, `rc-dropoff-date-${watchRcPickupDate}`)
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "[grid-area:dropoffTime]",
                                    children: /*#__PURE__*/ jsx(Select, {
                                        label: "Drop-off Time",
                                        isInset: true,
                                        menuItems: timeSelectOptions,
                                        initialValue: dropoffTime,
                                        onChange: (item)=>{
                                            setDropoffTime(item);
                                            setValue('rcDropoffTime', item, {
                                                shouldDirty: true
                                            });
                                            trigger('rcDropoffTime');
                                        },
                                        placeholder: "Drop-off Time",
                                        isError: !!errors.rcDropoffTime?.message,
                                        errorTooltipText: errors.rcDropoffTime?.message
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx(Button, {
                            buttonType: "submit",
                            type: "primary",
                            size: "lg",
                            className: cn('[grid-area:submit] @3xl:[grid-area:submit]', '@7xl:[grid-area:unset]', isMobile && 'landing' !== pageName ? 'fixed right-4 bottom-4 left-4' : ''),
                            children: "Find Your Trip"
                        })
                    ]
                })
            })
        })
    });
}
export { PackageSearchForm };
